import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Tag, Filter, ArrowLeft } from 'lucide-react';
import { useNews } from '../../hooks/useNews';
import { useNewsCategories } from '../../hooks/useNewsCategories';
import { useNewsTags } from '../../hooks/useNewsTags';
import { NewsArticleCard } from '../../components/news/NewsArticleCard';
import type { NewsArticleWithRelations, NewsCategory, NewsTag } from '../../types/news';

const NewsArchive = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState<NewsArticleWithRelations[]>([]);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [tags, setTags] = useState<NewsTag[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    searchParams.get('category')
  );
  const [selectedTag, setSelectedTag] = useState<string | null>(
    searchParams.get('tag')
  );
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('search') || ''
  );
  const [loading, setLoading] = useState(true);

  const { fetchArticles } = useNews();
  const { fetchCategories } = useNewsCategories();
  const { fetchTags } = useNewsTags();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadData();
  }, []);

  // Atualizar URL quando os filtros mudarem
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedTag) params.set('tag', selectedTag);
    if (searchQuery) params.set('search', searchQuery);
    setSearchParams(params);
  }, [selectedCategory, selectedTag, searchQuery]);

  const loadData = async () => {
    try {
      const [articlesData, categoriesData, tagsData] = await Promise.all([
        fetchArticles({
          category: selectedCategory || undefined,
          tag: selectedTag || undefined,
          search: searchQuery || undefined
        }),
        fetchCategories(),
        fetchTags()
      ]);
      setArticles(articlesData);
      setCategories(categoriesData);
      setTags(tagsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Recarregar dados quando os filtros mudarem
  useEffect(() => {
    loadData();
  }, [selectedCategory, selectedTag, searchQuery]);

  const handleCategorySelect = (slug: string | null) => {
    setSelectedCategory(slug);
    if (slug === selectedCategory) {
      setSelectedCategory(null);
    }
  };

  const handleTagSelect = (slug: string | null) => {
    setSelectedTag(slug);
    if (slug === selectedTag) {
      setSelectedTag(null);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleArticleClick = (slug: string) => {
    navigate(`/news/${slug}`);
  };

  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedTag(null);
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar às notícias
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h1 className="text-4xl font-bold mb-4">Arquivo de Notícias</h1>
          <p className="text-xl text-gray-300">
            Explore todas as notícias do AEAAV
          </p>
        </motion.div>

        <div className="mb-8 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Pesquisar notícias..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2 relative z-0">
            <Filter className="w-5 h-5 text-primary shrink-0" />
            <button
              onClick={() => handleCategorySelect(null)}
              className={`px-4 py-2 rounded-full transition-colors whitespace-nowrap ${
                !selectedCategory
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              Todas as Categorias
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category.slug)}
                className={`px-4 py-2 rounded-full transition-colors whitespace-nowrap ${
                  selectedCategory === category.slug
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary hover:bg-secondary/80'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2 relative z-0">
            <Tag className="w-5 h-5 text-primary shrink-0" />
            <button
              onClick={() => handleTagSelect(null)}
              className={`px-4 py-2 rounded-full transition-colors whitespace-nowrap ${
                !selectedTag
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              Todas as Tags
            </button>
            {tags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => handleTagSelect(tag.slug)}
                className={`px-4 py-2 rounded-full transition-colors whitespace-nowrap ${
                  selectedTag === tag.slug
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary hover:bg-secondary/80'
                }`}
              >
                {tag.name}
              </button>
            ))}
          </div>

          {(selectedCategory || selectedTag || searchQuery) && (
            <div className="flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-400 hover:text-primary transition-colors"
              >
                Limpar filtros
              </button>
            </div>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {articles.map((article) => (
            <NewsArticleCard
              key={article.id}
              article={article}
              onClick={() => handleArticleClick(article.slug)}
            />
          ))}
        </div>

        {articles.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">Nenhuma notícia encontrada.</p>
            {(selectedCategory || selectedTag || searchQuery) && (
              <button
                onClick={clearFilters}
                className="mt-4 text-primary hover:text-primary/80 transition-colors"
              >
                Limpar filtros e mostrar todas as notícias
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsArchive;